import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/cms/catalog-categories';
export const getCmsCatalogCategoriesEndpointIdentifier = endpoint;

export interface CmsCatalogCategoryDto {
  id: number;
  parentId: number;
  depth: number;
  title: string | null;
}

interface ResponseData {
  data: CmsCatalogCategoryDto[];
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, { parentId?: number; depth?: number }];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export interface CmsCatalogCategoriesParams {
  parentId?: number;
  depth?: number;
}

/**
 * GET /v1/cms/catalog-categories
 * - No params → depth-0 root categories (1-2 rows expected)
 * - parent_id → direct children of that category
 * - depth (0-10) → all categories at that depth
 */
export function useGetCmsCatalogCategoriesApi(
  params: CmsCatalogCategoriesParams = {},
  options?: Options
): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();
  const { parentId, depth } = params;

  const isRoots = parentId === undefined && depth === undefined;

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint, {
        params: {
          parent_id: parentId,
          depth,
        },
      });

      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([endpoint, { parentId, depth }], queryFn, {
    enabled: isLoggedIn,
    // Roots are stable (1-2 rows, depth 0) — cache briefly so reopening the modal doesn't refetch.
    // Children levels change rarely but admin UX expects fresh data per interaction.
    staleTime: isRoots ? 30_000 : 0,
    ...options,
  });
}
